import { useEffect, useState } from "react";
import { Lightbulb, CheckCircle2, XCircle, Bug, Clock, Coins, Building2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function Recomendaciones() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [schoolFilter, setSchoolFilter] = useState("");
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/recommendations/all");
            setItems(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error cargando");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        api.get("/ejecutivo/schools").then((r) => setSchools(r.data || [])).catch(() => {});
    }, []);

    async function approve(id) {
        try {
            await api.post(`/recommendations/${id}/approve`);
            toast.success("Recomendación aprobada y +10 monedas otorgadas al alumno");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        }
    }
    async function reject(id) {
        if (!window.confirm("¿Rechazar esta recomendación?")) return;
        try {
            await api.post(`/recommendations/${id}/reject`);
            toast.success("Recomendación rechazada");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error");
        }
    }

    const filtered = items.filter((r) => {
        if (filter !== "all" && r.status !== filter) return false;
        if (schoolFilter && r.school !== schoolFilter) return false;
        return true;
    });
    const counts = {
        pending: items.filter((r) => r.status === "pending").length,
        approved: items.filter((r) => r.status === "approved").length,
        rejected: items.filter((r) => r.status === "rejected").length,
        all: items.length,
    };

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="ejecutivo-recomendaciones-page">
            <h1 className="text-3xl font-semibold mb-1 inline-flex items-center gap-2">
                <Lightbulb size={26} className="text-edu-gold" /> Recomendaciones de Alumnos
            </h1>
            <p className="text-edu-body mb-6">
                Valida o rechaza las recomendaciones y reportes enviados por estudiantes. Al aprobar, se otorgan automáticamente 10 monedas al autor.
            </p>

            <div className="flex flex-wrap gap-3 mb-5 items-end">
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-edu-muted font-semibold mb-1 block inline-flex items-center gap-1">
                        <Building2 size={11} /> Colegio
                    </label>
                    <select
                        className="edu-input min-w-[220px]"
                        value={schoolFilter}
                        onChange={(e) => setSchoolFilter(e.target.value)}
                        data-testid="filter-school"
                    >
                        <option value="">Todos los colegios</option>
                        {schools.map((s) => (
                            <option key={s.id} value={s.name}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex gap-2 flex-wrap" data-testid="filter-status">
                    {["pending", "approved", "rejected", "all"].map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-xl text-sm inline-flex items-center gap-2 ${
                                filter === f
                                    ? "bg-edu-navy text-white"
                                    : "bg-edu-card border border-edu hover:bg-gray-50"
                            }`}
                            data-testid={`filter-${f}`}
                        >
                            <span>
                                {f === "pending"
                                    ? "Pendientes"
                                    : f === "approved"
                                      ? "Aprobadas"
                                      : f === "rejected"
                                        ? "Rechazadas"
                                        : "Todas"}
                            </span>
                            <span
                                className={`text-xs px-1.5 py-0.5 rounded-full ${
                                    filter === f ? "bg-white/20" : "bg-edu-soft"
                                }`}
                            >
                                {counts[f]}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="space-y-3">
                {loading && <p className="text-edu-muted">Cargando…</p>}
                {!loading && filtered.length === 0 && (
                    <p className="text-edu-muted" data-testid="empty-state">
                        Sin recomendaciones en esta categoría.
                    </p>
                )}
                {filtered.map((r) => (
                    <div key={r.id} className="edu-card p-5" data-testid={`rec-${r.id}`}>
                        <div className="flex items-center justify-between mb-2 gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="edu-pill bg-edu-navy/10 text-edu-navy inline-flex items-center gap-1">
                                    {r.type === "bug" ? <Bug size={11} /> : <Lightbulb size={11} />}
                                    {r.type === "bug" ? "Bug" : "Recomendación"}
                                </span>
                                <StatusPill status={r.status} />
                            </div>
                            <span className="text-xs text-edu-muted">
                                {(r.created_at || "").slice(0, 10)}
                            </span>
                        </div>
                        <p className="text-sm font-medium mb-1">{r.user_name}</p>
                        <p className="text-xs text-edu-muted mb-2">Colegio: {r.school || "—"}</p>
                        <p className="text-sm text-edu-body whitespace-pre-line mb-3">{r.content}</p>
                        {r.status === "approved" && (
                            <p className="text-xs text-emerald-700 inline-flex items-center gap-1 mb-2">
                                <Coins size={12} /> +{r.reward} monedas otorgadas
                            </p>
                        )}
                        {r.status === "pending" && (
                            <div className="flex gap-2">
                                <button
                                    onClick={() => approve(r.id)}
                                    className="edu-btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1"
                                    data-testid={`approve-${r.id}`}
                                >
                                    <CheckCircle2 size={12} /> Aprobar
                                </button>
                                <button
                                    onClick={() => reject(r.id)}
                                    className="px-3 py-1.5 text-xs rounded-xl bg-red-100 text-red-700 inline-flex items-center gap-1"
                                    data-testid={`reject-${r.id}`}
                                >
                                    <XCircle size={12} /> Rechazar
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { label: "Pendiente", cls: "bg-yellow-100 text-yellow-800", Icon: Clock },
        approved: { label: "Aprobada", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
        rejected: { label: "Rechazada", cls: "bg-red-100 text-red-700", Icon: XCircle },
    };
    const m = map[status] || map.pending;
    const Icon = m.Icon;
    return (
        <span className={`edu-pill ${m.cls} inline-flex items-center gap-1`}>
            <Icon size={11} /> {m.label}
        </span>
    );
}
